import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, X, Film, Images } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tiktok`;
const PRIVACY_RO = {
  PUBLIC_TO_EVERYONE: "Public",
  MUTUAL_FOLLOW_FRIENDS: "Prieteni",
  FOLLOWER_OF_CREATOR: "Urmăritori",
  SELF_ONLY: "Doar eu",
};
const STATUS_RO = { pending: "De aprobat", publishing: "Se publică…", published: "Publicat", failed: "Eșuat" };

export const TikTokQueueCard = ({ item, creator, onChange }) => {
  const [title, setTitle] = useState(item.title || "");
  const [desc, setDesc] = useState(item.description || "");
  const [privacy, setPrivacy] = useState("");
  const [comments, setComments] = useState(!creator?.comment_disabled);
  const [duet, setDuet] = useState(!creator?.duet_disabled);
  const [stitch, setStitch] = useState(!creator?.stitch_disabled);
  const [ownBrand, setOwnBrand] = useState(true);
  const [busy, setBusy] = useState(false);
  const editable = ["pending", "failed"].includes(item.status);
  const id = item.id;

  const publish = async () => {
    if (!privacy) return toast.error("Alege vizibilitatea");
    setBusy(true);
    try {
      await axios.put(`${API}/queue/${id}`, { title, description: desc });
      await axios.post(`${API}/queue/${id}/publish`, {
        privacy_level: privacy, disable_comment: !comments, disable_duet: !duet, disable_stitch: !stitch,
        brand_organic_toggle: ownBrand, brand_content_toggle: false,
      });
      toast.success("Se publică pe TikTok…");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const skip = async () => { await axios.post(`${API}/queue/${id}/skip`); onChange(); };

  return (
    <div data-testid={`tiktok-item-${id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-100">
          {item.kind === "video" ? <Film className="h-4 w-4 text-pink-400" /> : <Images className="h-4 w-4 text-cyan-400" />}
          <span className="truncate">{item.product_title || "Postare"}</span>
        </div>
        <span data-testid={`tiktok-item-status-${id}`} className="rounded-full bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-300">{STATUS_RO[item.status] || item.status}</span>
      </div>
      {item.kind === "photo" ? (
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {(item.photos || []).map((u, i) => (
            <img key={u} src={u} alt={`poza ${i + 1}`} className="h-40 w-[90px] flex-shrink-0 rounded-md border border-zinc-800 object-cover" />
          ))}
        </div>
      ) : (
        <p className="mb-3 rounded-md bg-zinc-950 px-3 py-2 text-xs text-zinc-400">Video din Drive: <span className="text-zinc-200">{item.video?.name}</span></p>
      )}
      {item.kind === "photo" && (
        <input data-testid={`tiktok-title-${id}`} disabled={!editable} value={title} maxLength={90} onChange={(e) => setTitle(e.target.value)}
          className="mb-2 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-100" placeholder="Titlu (max 90)" />
      )}
      <textarea data-testid={`tiktok-desc-${id}`} disabled={!editable} value={desc} onChange={(e) => setDesc(e.target.value)} rows={5}
        className="mb-3 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-200" />
      {item.error && <p data-testid={`tiktok-error-${id}`} className="mb-3 rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{item.error}</p>}
      {item.status === "published" && (
        <p className="mb-1 text-xs text-emerald-300">Publicat ({PRIVACY_RO[item.privacy_level] || item.privacy_level})
          {item.stats ? ` · ${item.stats.view_count ?? 0} vizualizări · ${item.stats.like_count ?? 0} like-uri` : ""}</p>
      )}
      {editable && (
        <div className="space-y-3 border-t border-zinc-800 pt-3 text-xs text-zinc-300">
          <p>Cont: <span className="font-medium text-zinc-100">@{creator?.creator_username || creator?.creator_nickname || "—"}</span></p>
          <select data-testid={`tiktok-privacy-${id}`} value={privacy} onChange={(e) => setPrivacy(e.target.value)}
            className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
            <option value="">Alege cine vede postarea…</option>
            {(creator?.privacy_level_options || []).map((p) => <option key={p} value={p}>{PRIVACY_RO[p] || p}</option>)}
          </select>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={comments} disabled={creator?.comment_disabled} onChange={(e) => setComments(e.target.checked)} data-testid={`tiktok-comments-${id}`} />Comentarii</label>
            {item.kind === "video" && <>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={duet} disabled={creator?.duet_disabled} onChange={(e) => setDuet(e.target.checked)} data-testid={`tiktok-duet-${id}`} />Duet</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={stitch} disabled={creator?.stitch_disabled} onChange={(e) => setStitch(e.target.checked)} data-testid={`tiktok-stitch-${id}`} />Stitch</label>
            </>}
          </div>
          <label className="flex items-start gap-1.5"><input type="checkbox" checked={ownBrand} onChange={(e) => setOwnBrand(e.target.checked)} data-testid={`tiktok-brand-${id}`} className="mt-0.5" />
            Conținut comercial: promovez propria marcă (BRAGUS). Postarea va fi etichetată „Conținut promoțional”.</label>
          <div className="flex justify-end gap-2">
            <button onClick={skip} data-testid={`tiktok-skip-${id}`} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-3 py-1.5 text-zinc-300"><X className="h-3.5 w-3.5" />Sari</button>
            <button onClick={publish} disabled={busy || !privacy} data-testid={`tiktok-publish-${id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-pink-600 px-4 py-1.5 font-medium text-white disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Publică
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
